<div class="website-content" style="max-width:72rem;padding:0 var(--wa-spacing-x-large) var(--wa-spacing-x-large) var(--wa-spacing-x-large);">
	<div class="wa-stack wa-gap-xl">
		<div class="wa-stack wa-gap-m content-section">
			<wa-divider class="section-divider"></wa-divider>
			<div class="wa-stack wa-gap-s">
				<div class="hero-eyebrow" waCaption="s" waFontWeight="semibold" waText>QUICK START</div>
				<h2 waHeading="l" waText>Getting Started with JWebMP</h2>
				<p waBody="m" waColorText="quiet" waText>Choose your mode. Follow the steps. Ship your app.</p>
			</div>
			<div class="wa-stack wa-gap-m">
				<p waBody="m" waText>This guide walks you through creating a JWebMP application from scratch. JWebMP supports two running modes &mdash; choose the one that fits your project:</p>
				<div *ngIf="!useGradle">
					<div waCaption="s" waFontWeight="semibold" waText>Prerequisites</div>
					<div class="wa-cluster wa-gap-s">
						<a href="https://adoptium.net" rel="noopener noreferrer" style="text-decoration:none;" target="_blank">
							<wa-tag pill variant="brand">Java 25+</wa-tag>
						</a>
						<a href="https://maven.apache.org" rel="noopener noreferrer" style="text-decoration:none;" target="_blank">
							<wa-tag pill variant="neutral">Maven 3.9+</wa-tag>
						</a>
						<a href="https://nodejs.org" rel="noopener noreferrer" style="text-decoration:none;" target="_blank">
							<wa-tag pill variant="warning">Node.js 22+</wa-tag>
						</a>
					</div>
				</div>
				<div *ngIf="useGradle">
					<div waCaption="s" waFontWeight="semibold" waText>Prerequisites</div>
					<div class="wa-cluster wa-gap-s">
						<a href="https://adoptium.net" rel="noopener noreferrer" style="text-decoration:none;" target="_blank">
							<wa-tag pill variant="brand">Java 25+</wa-tag>
						</a>
						<a href="https://gradle.org" rel="noopener noreferrer" style="text-decoration:none;" target="_blank">
							<wa-tag pill variant="neutral">Gradle 8.6+</wa-tag>
						</a>
						<a href="https://nodejs.org" rel="noopener noreferrer" style="text-decoration:none;" target="_blank">
							<wa-tag pill variant="warning">Node.js 22+</wa-tag>
						</a>
					</div>
				</div>
				<div class="wa-stack wa-gap-m">
					<wa-details summary="Angular Mode (Client-Side/MicroFront SPA)">
						<div class="wa-stack wa-gap-m">
							<p waBody="m" waText>Include the Angular plugin and the build plugin generates a complete Angular 21 single-page application. Node.js is required at build time for the Angular CLI. The resulting SPA is static and can be served from any host or CDN &mdash; the JWebMP server is entirely optional.</p>
							<markdown [data]="mdContent_3" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
							<div *ngIf="!useGradle" class="code-block-wrapper">
								<div class="code-block-label" waCaption="s" waFontWeight="semibold" waText>Angular mode &mdash; Maven plugin &#40;pom.xml&#41;</div>
								<markdown [data]="mdContent_4" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
							</div>
							<div *ngIf="useGradle" class="code-block-wrapper">
								<div class="code-block-label" waCaption="s" waFontWeight="semibold" waText>Angular mode &mdash; Gradle plugin &#40;build.gradle.kts&#41;</div>
								<markdown [data]="mdContent_5" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
							</div>
							<div waCaption="s" waColorText="quiet" waFontWeight="semibold" waText>The plugin discovers your annotated classes, generates TypeScript, installs npm dependencies, and runs the Angular CLI &mdash; all in one build step.</div>
						</div>
					</wa-details>
					<wa-details summary="Hosted Mode (JWebMP + GuicedEE Backend)">
						<div class="wa-stack">
							<p waBody="m" waText>JWebMP hosts the application on a Vert.x 5 endpoint alongside your GuicedEE backend. REST APIs, WebSockets, authentication, and persistence all run on the same process. Add the Angular plugin to serve a compiled SPA from the same endpoint, or render pages server-side.</p>
							<markdown [data]="mdContent_6" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
						</div>
					</wa-details>
				</div>
			</div>
		</div>
		<div class="wa-stack wa-gap-m content-section">
			<wa-divider class="section-divider"></wa-divider>
			<div class="wa-stack wa-gap-s">
				<div class="hero-eyebrow" waCaption="s" waFontWeight="semibold" waText>YOUR FIRST COMPONENT</div>
				<h2 waHeading="l" waText>Java Class In, Angular Component Out</h2>
				<p waBody="m" waColorText="quiet" waText>The same component code works in both modes.</p>
			</div>
			<div class="wa-stack wa-gap-m">
				<p waBody="m" waText>Create your first page component. This code is identical for both modes &mdash; in Angular mode the build generates an Angular component; in hosted mode the same class is served by the Vert.x host. &#64;NgComponent gives it a selector. &#64;NgRoutable assigns its URL. The constructor builds the component tree in Java.</p>
				<div class="code-block-wrapper">
					<div class="code-block-label" waCaption="s" waFontWeight="semibold" waText>MyHomePage.java</div>
					<markdown [data]="mdContent_7" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
				</div>
			</div>
		</div>
	</div>
</div>